import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const AVAILABILITY_URL = "/guide/availability"

/**
 * The availability calendar API. The full contract, including the day model and the reason types,
 * lives in `docs/availability-calendar-api.md`.
 *
 * @typedef {Object} AvailabilitySlot
 * @property {string} time            "HH:mm", business timezone (A4)
 * @property {boolean} is_available
 * @property {number} [spots_left]    ticketed listings only
 * @property {number} [capacity]
 * @property {number} [booked]
 *
 * @typedef {Object} AvailabilityReason
 * @property {string} type            see §2 — crew_shared, resource_shared, blackout, crew_off, …
 * @property {string} label           server copy, rendered as-is and not translated
 * @property {string} [detail]
 * @property {string} [blackout_id]   blackout reasons only; matches a record from /guide/blackouts
 * @property {string} [scope]         blackout reasons only
 *
 * @typedef {Object} AvailabilityDay
 * @property {string} date            "YYYY-MM-DD"
 * @property {"available"|"limited"|"fully_booked"|"not_available"} status
 * @property {AvailabilitySlot[]} slots
 * @property {number} slots_total
 * @property {number} bookings_count
 * @property {AvailabilityReason[]} reasons
 */

const requestedCalendar = createAction("availability/requestedCalendar")
const requestedDayBookings = createAction("availability/requestedDayBookings")
const receivedDayBookings = createAction("availability/receivedDayBookings")
const failedDayBookings = createAction("availability/failedDayBookings")
export const clearedDayBookings = createAction("availability/clearedDayBookings")
const receivedCalendar = createAction("availability/receivedCalendar")
const failedCalendar = createAction("availability/failedCalendar")
export const clearedCalendar = createAction("availability/clearedCalendar")

const initialState = {
  // Day-resolved availability for one listing, keyed "YYYY-MM-DD".
  calendar: { ...LOADING_INITIAL_STATE, days: {}, listing: null },
  // The reservations the day panel lists, for one listing on one day.
  dayBookings: { ...LOADING_INITIAL_STATE, list: [] }
}

/**
 * Unwraps the payload.
 *
 * Both envelopes are accepted for the same reason the blackout store accepts both: the sibling
 * blackout endpoints shipped their payload under `message` where the contract said `data`, and a
 * plain `{ message: "…" }` string body must still fall through rather than be read as the payload.
 */
const toRecord = (payload) => {
  if (!payload || typeof payload !== "object") return payload
  if (payload.data && typeof payload.data === "object") return payload.data
  if (payload.message && typeof payload.message === "object") return payload.message
  return payload
}

/**
 * Indexes `days: [{ date, … }]` by date.
 *
 * Every calendar surface asks about one day at a time, so the list is indexed once here rather
 * than searched per cell. A payload that is already keyed passes through, and anything else —
 * most importantly an error body from an endpoint that is not built yet — collapses to `{}`.
 */
export const toAvailabilityDays = (payload) => {
  const data = toRecord(payload)
  const days = data?.days

  if (Array.isArray(days)) {
    return days.reduce((indexed, day) => {
      if (day?.date) indexed[day.date] = day
      return indexed
    }, {})
  }
  return days && typeof days === "object" ? days : {}
}

/**
 * The booking feed's list, whichever envelope it arrives in.
 *
 * `/guide/calendar/bookings` has shipped the array bare, under `data` and under `bookings`, so all
 * three are accepted rather than the panel silently listing nothing.
 */
export const toDayBookings = (payload) => {
  const data = toRecord(payload)
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.bookings)) return data.bookings
  return []
}

const availabilityReducer = createReducer(initialState, {
  [requestedCalendar.type]: (state) => setRequested(state.calendar),
  [receivedCalendar.type]: (state, { payload }) => {
    setReceived(state.calendar)
    state.calendar.days = toAvailabilityDays(payload)
    state.calendar.listing = toRecord(payload)?.listing || null
  },
  [failedCalendar.type]: (state) => {
    setFailed(state.calendar)
    state.calendar.days = {}
  },
  [clearedCalendar.type]: (state) => {
    state.calendar = { ...initialState.calendar }
  },
  [requestedDayBookings.type]: (state) => setRequested(state.dayBookings),
  [receivedDayBookings.type]: (state, { payload }) => {
    setReceived(state.dayBookings)
    state.dayBookings.list = toDayBookings(payload)
  },
  [failedDayBookings.type]: (state) => {
    setFailed(state.dayBookings)
    state.dayBookings.list = []
  },
  [clearedDayBookings.type]: (state) => {
    state.dayBookings = { ...initialState.dayBookings, list: [] }
  }
})

/**
 * GET /guide/availability/calendar?listing_id=&start_date=&end_date=
 *
 * `listing_id` is required — availability is a per-listing question and has no meaning across
 * listings (A1). The window is the visible grid, which for a month view starts and ends outside
 * the month itself.
 *
 * Errors are surfaced through the reducer's `failed` flag rather than a notification: the page
 * shows an inline error in the grid, and a toast on every month step would be noise.
 */
export const getAvailabilityCalendar = (params = {}) => (dispatch) => {
  const actions = { onStart: requestedCalendar.type, onSuccess: receivedCalendar.type, onError: failedCalendar.type }
  return dispatch(api({ url: `${AVAILABILITY_URL}/calendar`, method: "get", params, ...actions, suppressErrorNotification: true }))
}

/**
 * GET /guide/calendar/bookings, narrowed to one listing on one day.
 *
 * The availability feed counts a day's reservations but does not list them, and the booking
 * calendar's own endpoint already returns exactly the rows the panel draws. It is dispatched from
 * here rather than through `getCalendarBookings` because that thunk writes the booking calendar's
 * slice — sharing it would leave that page holding a single listing's day when it next rendered.
 */
export const getAvailabilityDayBookings = (params = {}) => {
  const actions = { onStart: requestedDayBookings.type, onSuccess: receivedDayBookings.type, onError: failedDayBookings.type }
  return api({ url: "/guide/calendar/bookings", method: "get", params, ...actions, suppressErrorNotification: true })
}

export default availabilityReducer
